import DatePicker from '@anm/components/DatePicker';
import { UploadFileProps } from '@anm/components/InputFile';
import UploadImageWithPreview from '@anm/components/UploadImageWithPreview';
import Input from '@anm/components/form/Input';
import Switch from '@anm/components/form/Switch';
import Select from '@anm/components/select/Select';
import noop from '@anm/helpers/noop';
import { useCallback, FC } from 'react';

import { ButtonWrapper, FieldCounter, FolderPathInput, Label } from '../../../StreamModalWrapper';
import { StreamDetailsStepProps, StreamScreens } from '../../../types';
import useValidateTextField, { max_description_length } from '../../hooks/useValidateTextField';
import Header from '../Header';

import {
  ButtonsContainer,
  DateFieldWrapper,
  DatePickerWrapper,
  DescriptionField,
  Hint,
  NewStreamConfigsWrapper,
  NewStreamInputsWrapper,
  SaveButton,
  ScheduleSection,
  SwitchContainer,
  TimeAndDateContainer,
  TimeContainer,
  TimeRow,
  UploadContainer,
  WarnMessage
} from './Wrapper';

const StreamDetailsStep: FC<StreamDetailsStepProps> = ({
  name,
  date,
  path,
  isEdit,
  preview,
  isHosted,
  isPending,
  minsValue,
  hoursValue,
  minsOptions,
  isRecording,
  description,
  isScheduled,
  isDuplicate,
  titleMaxLength,
  isSubmitPending,
  canGuestAddDestination,
  isDisabledSubmitButton,
  onAddGuestDestinationChange,
  onSelectPath,
  hoursOptions,
  timeTypeValue,
  isBackButton,
  isSetupStream,
  timeTypeOptions,
  onDateChange,
  navigateTo,
  onNameChange,
  onCreateStream,
  onTimeTypeChange,
  handleMinsChange,
  handleHoursChange,
  toggleScheduled,
  onPreviewSelect,
  onDescriptionChange,
  destinationsList
}) => {
  const title = isEdit && !isDuplicate ? 'Edit stream details' : 'Stream details';
  const [isNameError, validateName, handleNameChange] = useValidateTextField({
    text: name,
    titleMaxLength,
    onTextChange: onNameChange
  });
  const [isDescriptionError, validateDescription, handleDescriptionChange] = useValidateTextField({
    text: description,
    onTextChange: onDescriptionChange
  });

  const handleCreateStream = useCallback(() => {
    const isValidName = validateName({ titleMaxLength });
    const isValidDescription = validateDescription();

    isValidName && isValidDescription && onCreateStream(preview.src);
  }, [onCreateStream, validateName, preview.src, titleMaxLength]);

  const handleSetupDestinations = () => {
    const isValidName = validateName({ titleMaxLength });
    const isValidDescription = validateDescription();

    isValidName && isValidDescription && navigateTo(StreamScreens.stream_destinations);
  };

  const submitButtonText = isEdit && !isDuplicate && !isRecording ? 'Edit destinations' : 'Setup destinations';

  const fbRescheduleWarning =
    isEdit &&
    !isDuplicate &&
    !isRecording &&
    destinationsList?.find(d => d.network === 'FACEBOOK' && d.kind === 'group');

  const thumbnailHint = 'Upload a thumbnail for social media announcement post.';

  return (
    <>
      <Header
        {...{ title }}
        onBack={isBackButton && !isSetupStream ? () => navigateTo(StreamScreens.new_stream) : undefined}
      />
      <NewStreamInputsWrapper>
        <Label>
          <FieldCounter isMax={name.length >= titleMaxLength}>
            {name.length} / {titleMaxLength}
          </FieldCounter>
          <span>Event title</span>
          <Input
            variant="simple-small"
            hasError={isNameError || titleMaxLength < name.length}
            onChange={handleNameChange}
            value={name}
          />
        </Label>
        <Label>
          <FieldCounter isMax={description.length >= max_description_length}>
            {description.length} / {max_description_length}
          </FieldCounter>
          <span>Description</span>
          <DescriptionField
            variant="simple"
            value={description}
            hasError={isDescriptionError}
            onChange={handleDescriptionChange}
            placeholder="Add a description"
          />
        </Label>
        {isSetupStream && (
          <Label style={{ marginBottom: '25px' }}>
            <span>Select folder for recording</span>
            <FolderPathInput variant="simple-small" value={path} onClick={onSelectPath} onChange={noop} />
          </Label>
        )}
        <UploadContainer>
          <Hint>
            <p>{thumbnailHint}</p>
            <span>.png or .jpg at least 1280x720</span>
          </Hint>
          <UploadImageWithPreview
            title={name}
            isPending={isPending}
            previewSrc={preview.src}
            onImageUpload={(p: UploadFileProps[]) => onPreviewSelect(p[0])}
          />
        </UploadContainer>

        <SwitchContainer>
          <Switch checked={isScheduled} onChange={toggleScheduled} />
          <span>Schedule for later and create a live event on social media</span>
        </SwitchContainer>

        {isScheduled && (
          <>
            <ScheduleSection>
              {fbRescheduleWarning && (
                <WarnMessage variant="danger" position="left">
                  If you are re-scheduling this stream on Facebook Group you may need to delete the old event manually.
                  We apologize for the inconvenience.
                </WarnMessage>
              )}
            </ScheduleSection>
            <NewStreamConfigsWrapper>
              <Hint>When your stream is starting?</Hint>
              <TimeAndDateContainer>
                <DateFieldWrapper>
                  <Label>Date</Label>
                  <DatePickerWrapper>
                    <DatePicker
                      isOneCalendar
                      isRangeDate={false}
                      disableDates="past"
                      contentDirection="to-top"
                      onDateApply={dates => onDateChange({ dates, isDatePicker: true })}
                      defaultDates={[date]}
                    />
                  </DatePickerWrapper>
                </DateFieldWrapper>
                <TimeContainer>
                  <Label>Time, {Intl.DateTimeFormat().resolvedOptions().timeZone}</Label>
                  <TimeRow>
                    <Select name="time_hours" value={hoursValue} options={hoursOptions} onChange={handleHoursChange} />
                    <Select name="time_mins" value={minsValue} options={minsOptions} onChange={handleMinsChange} />
                    <Select
                      name="time_type"
                      value={timeTypeValue}
                      options={timeTypeOptions}
                      onChange={onTimeTypeChange}
                    />
                  </TimeRow>
                </TimeContainer>
              </TimeAndDateContainer>
            </NewStreamConfigsWrapper>
          </>
        )}

        {!isHosted && (
          <SwitchContainer>
            <Switch checked={!!canGuestAddDestination} onChange={onAddGuestDestinationChange} />
            <span>Allow guests to co-stream this event to their destinations</span>
          </SwitchContainer>
        )}
      </NewStreamInputsWrapper>
      <ButtonsContainer>
        <ButtonWrapper size="big" variant="silver-border" onClick={handleSetupDestinations}>
          {submitButtonText}
        </ButtonWrapper>
        {isEdit && (
          <SaveButton
            size="big"
            isPending={isSubmitPending || isPending}
            variant="blue-saturated"
            onClick={handleCreateStream}
            disabled={isDisabledSubmitButton}
          >
            Save
          </SaveButton>
        )}
      </ButtonsContainer>
    </>
  );
};

export default StreamDetailsStep;
